import React from 'react';
import { Loader, Table, useToasts } from 'freemium-ui';
import { History } from 'history';
import { get } from 'lodash';
import qs from 'query-string';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  GLOBAL_PER_PAGE,
  ON_CLICK_OPEN_COLUMNS,
  SEGMENTS_LIMIT,
} from 'routes/constants';
import { useGetAllUsers } from 'routes/screens/settings/screens/user/hooks';
import { useExecuteSegment } from 'shared/hooks';
import { Center } from 'shared/styles';
import {
  AccountInfoType,
  AllUsersType,
  BreadcrumpDataType,
  ConfirmationType,
  ContactFormType,
  FormServFieldType,
  McrContactType,
  MetaType,
  RuleType,
  SegmentResponseType,
  SegmentType,
  UserType,
} from 'shared/types';
import { getAllUsersFromResponse, getDefaultColumnOrder } from 'shared/utils';
import { useAccessControl } from 'utils/access-control';
import EmptyState from '../../../../../shared/components/EmptyState';
import {
  BACK_END_FIELDS,
  COLUMNS_HIDE_ORDERING_KEY,
} from '../../../../../shared/constants';
import HeaderComponent from '../../../../components/HeaderComponent';
import NewSegmentForm from '../../components/NewSegmentForm';
import { useLocalStorage } from '../../../../../hooks';
import { useGetContactLists } from '../../../lists/hooks';
import {
  useGetSegment,
  useGetSegmentCount,
  useGetSegmentsCount,
  useSaveSegment,
} from '../../hooks';
import { getTableColumnsFromFields } from '../../../../utils';
import { getModifiedSegmentData } from '../../utils';
import { SegmentContainer, SegmentPane, TablePane } from './styles';
import { getContactsColumnSettings } from '../../../contacts/constants';

type CreateSegmentProps = {
  contactForm: ContactFormType;
  setBreadcrumpData?: (
    data: BreadcrumpDataType | React.SetStateAction<BreadcrumpDataType>,
  ) => void;
  actionTime?: string;
  setConfirmationData: (
    str: ConfirmationType | React.SetStateAction<ConfirmationType>,
  ) => void;
  accountInfo?: AccountInfoType;
  userInfo?: UserType;
  contactsCount: number;
  fetchContactCount: () => void;
};

const defaultRule: RuleType = {
  match_type: 'ALL',
  condition_sets: [],
};

const CreateSegment = (props: CreateSegmentProps) => {
  const { t } = useTranslation();
  const {
    contactForm,
    setBreadcrumpData,
    actionTime,
    setConfirmationData,
    accountInfo,
    userInfo,
    contactsCount,
    fetchContactCount,
  } = props;
  const { segmentId }: { segmentId?: string } = useParams();
  const [, setToast] = useToasts();
  const { push }: History = useHistory();
  const routerLocation = useLocation();
  const { access } = useAccessControl();
  const userHasAccess = React.useMemo(() => {
    return (access || []).indexOf('contact:write') > -1;
  }, [access]);

  const [shouldFetchAllUsers, setShouldFetchAllUsers] =
    React.useState<boolean>(false);
  const { data } = useGetAllUsers({
    enabled: shouldFetchAllUsers,
  });
  const allUsers: AllUsersType = React.useMemo(
    () => getAllUsersFromResponse(data),
    [data],
  );

  const fields = React.useMemo(
    () =>
      contactForm?.fields?.filter(
        (contact) => !BACK_END_FIELDS.includes(contact.name),
      ) || [],
    [contactForm],
  );
  const columnSettings = React.useMemo(
    () =>
      getContactsColumnSettings({ allUsers, fields, accountInfo, userInfo }),
    [allUsers, fields, accountInfo],
  );

  const [executedRule, setExecutedRule] = React.useState<RuleType>(defaultRule);
  const [formValues, setFormValues] = React.useState<any>({});

  const [allFields, setAllFields] = React.useState<
    Record<string, FormServFieldType>
  >({});
  const [segmentData, setSegmentData] = React.useState<McrContactType[]>();
  const [meta, setMeta] = React.useState<MetaType>({});
  const [pageIndex, setPageIndex] = React.useState<number>(0);
  const [columnOrder, setColumnOrder] = useLocalStorage(
    'segments-create-column-order',
    getDefaultColumnOrder({
      fields,
      columnSettings,
    }),
  );
  const [hiddenColumns, setHiddenColumns] = useLocalStorage(
    'segments-create-hidden-columns',
    fields
      .filter(
        (field: FormServFieldType) =>
          !(
            columnSettings[field.name]?.showInitial ||
            columnSettings[field.name]?.disableReordering
          ),
      )
      .map((field) => field[COLUMNS_HIDE_ORDERING_KEY]),
  );

  const { data: segmentsCountData } = useGetSegmentsCount({});

  const { data: segmentCountData, refetch: refetchSegmentCount } =
    useGetSegmentCount({
      segmentId: segmentId || '',
    });

  const segmentContactsCount = Number(segmentCountData?.count);

  const {
    data: segment = {},
    isLoading: isLoadingSegment,
    isError: isSegmentError,
  } = useGetSegment({
    segmentId: segmentId || '',
  });

  const {
    mutate: executeSegment,
    isLoading: isExecutingSegment,
    isError: isExecuteSegmentError,
    isIdle,
    reset,
  } = useExecuteSegment({
    setToast,
    callback: (isSuccess: boolean, response: SegmentResponseType) => {
      if (isSuccess) {
        refetchSegmentCount();
        setSegmentData(response.contacts);
        if (response && response.meta) {
          setMeta(response.meta);
        }
      }
    },
  });

  const {
    data: contactListsData = {},
    isLoading: isLoadingContactLists,
    isError: isContactListsError,
  } = useGetContactLists();
  const { contactList: contactLists = [] } = contactListsData || {};

  const { mutate: saveSegment, isLoading: isSavingSegment } = useSaveSegment({
    setToast,
    callback: (
      isSuccess: boolean,
      response: SegmentType,
      payload: SegmentType,
    ) => {
      if (isSuccess && !payload?.id) {
        push('/segments');
      }
    },
  });

  const onPageClick = (pgNo: number) => {
    if (
      !executedRule ||
      !executedRule.condition_sets ||
      executedRule.condition_sets.length === 0
    )
      return;
    executeSegment({
      rule: executedRule,
      params: {
        page: pgNo,
      },
    });
  };

  const onSave = (val: SegmentType) => {
    const payload: SegmentType = {
      ...val,
      entity: 'CONTACT',
      segment_type: 'RULE_BASED',
    };
    saveSegment(payload);
  };

  React.useEffect(() => {
    if (
      !shouldFetchAllUsers &&
      ['creater_id', 'updater_id'].some(
        (cName) => !hiddenColumns.includes(cName),
      )
    ) {
      setShouldFetchAllUsers(true);
    }
  }, [hiddenColumns]);

  React.useEffect(() => {
    if ((segment && segment.id) || !routerLocation.search) return;
    const parsedValues = qs.parse(routerLocation.search, {
      parseBooleans: true,
      parseNumbers: true,
    });
    const { formValues: newFormValues, ...rest }: { formValues?: string } =
      parsedValues;
    if (!newFormValues) return;
    try {
      const parsed = JSON.parse(newFormValues);
      setFormValues(parsed);
      if (parsed && parsed.rule) {
        executeSegment({
          rule: parsed.rule,
          params: {
            page: pageIndex + 1,
          },
        });
      }
      setExecutedRule(parsed.rule);
      if (newFormValues || Object.keys(rest).length > 0) {
        const newURL = qs.stringifyUrl({
          url: routerLocation.pathname,
          query: { ...(rest || {}) },
        });
        push(newURL);
      }
    } catch (e) {}
  }, []);

  React.useEffect(() => {
    if (
      routerLocation.pathname === '/segments/create' &&
      segmentData &&
      segmentData.length >= 0
    ) {
      setExecutedRule(defaultRule);
      setSegmentData(undefined);
      setMeta({});
      setPageIndex(0);
      reset();
      setFormValues({ name: '', rule: defaultRule });
    }
  }, [routerLocation.pathname]);

  React.useEffect(() => {
    if (segment && segment.id && segment.rule) {
      executeSegment({
        rule: segment.rule,
        params: {
          page: pageIndex + 1,
        },
      });
      setExecutedRule(segment.rule);
    }
  }, [segment, actionTime]);

  React.useEffect(() => {
    if (setBreadcrumpData && segment?.name) {
      setBreadcrumpData((old) => ({
        ...old,
        path: segment?.name ? [segment.name] : [],
      }));
    }
  }, [segment]);

  React.useEffect(() => {
    if (!fields) return;
    const newAllFields = {};
    fields.forEach((field) => {
      newAllFields[field[COLUMNS_HIDE_ORDERING_KEY]] = field;
    });
    setAllFields(newAllFields);
  }, [fields]);

  React.useEffect(() => {
    onPageClick(pageIndex + 1);
  }, []);

  React.useEffect(() => {
    if (segmentId || !segmentsCountData || !accountInfo) return;
    if (
      (Number(`${segmentsCountData?.count}`) || 0) >=
      (Number(accountInfo?.resource_limit_configuration?.segments) ||
        SEGMENTS_LIMIT)
    ) {
      setToast({
        type: 'warning',
        delay: 3000,
        text: `${t('forms.toast.warning.segments_limit_reached')}`,
      });
      push('/segments');
    }
  }, [segmentsCountData]);

  React.useEffect(() => {
    if (segmentId && setBreadcrumpData && segmentCountData?.count) {
      setBreadcrumpData((old) => ({
        ...old,
        count: Number(`${segmentCountData?.count}`) || 0,
      }));
    }
  }, [segmentCountData]);

  const columns = React.useMemo(
    () =>
      getTableColumnsFromFields({
        fields,
        columnSettings,
        contactLists: contactLists || [],
        accountInfo,
      }),
    [fields, contactLists, columnSettings, accountInfo],
  );

  return (
    <SegmentContainer>
      <SegmentPane>
        {isLoadingSegment || isLoadingContactLists ? (
          <Center>
            <Loader />
          </Center>
        ) : (
          <>
            {isSegmentError ? (
              <Center>{t('segments.not_exists')}</Center>
            ) : (
              <NewSegmentForm
                segmentData={getModifiedSegmentData(segment)}
                onSave={onSave}
                cloneValue={formValues}
                disabled={!userHasAccess}
                executeSegment={(rule) => {
                  setExecutedRule(rule);
                  executeSegment({
                    rule,
                    params: {
                      page: 1,
                    },
                    callback: (isSuccess: boolean) => {
                      if (isSuccess) {
                        setPageIndex(0);
                      }
                    },
                  });
                }}
                isExecutingSegment={isExecutingSegment}
                contactForm={contactForm}
                isSavingSegment={isSavingSegment}
                userInfo={userInfo}
              />
            )}
          </>
        )}
      </SegmentPane>
      <TablePane>
        <Table
          data={segmentData || []}
          columns={columns}
          count={meta.totalNumberOfRecords || 0}
          isLoading={
            !segmentData && !isExecuteSegmentError && !(!segmentId && isIdle)
          }
          loaderStyles={{ minHeight: 'calc(100vh - 136px)' }}
          hideFilters
          hideTableOnEmpty
          disableRowSelect
          pageIndex={pageIndex}
          isFetching={segmentData ? isExecutingSegment : false}
          pageSize={GLOBAL_PER_PAGE}
          hiddenColumns={hiddenColumns}
          columnOrder={columnOrder}
          setPageIndex={(pgIdx: number) => {
            onPageClick(pgIdx + 1);
            setPageIndex(pgIdx);
          }}
          cellPropGetter={(cell: any) => {
            const field = allFields[cell.column.id] || {};
            return {
              className: get(field, ['name'], ''),
              title:
                field && field.name && field.visible
                  ? get(cell, ['row', 'original', field?.name])
                  : '',
            };
          }}
          onRowClick={(row: any, column: any) => {
            const field = allFields[column.id] || {};
            if (field && ON_CLICK_OPEN_COLUMNS.includes(field.name)) {
              push(`/contacts/${get(row, ['original', 'id'], '')}`);
            }
          }}
          headerComponent={
            <HeaderComponent
              type="segment"
              columnOrder={columnOrder}
              hideSearch
              setConfirmationData={setConfirmationData}
              rule={executedRule}
              contactLists={contactLists || []}
              setColumnOrder={(cols: string[]) => setColumnOrder(cols)}
              hiddenColumns={hiddenColumns}
              setHiddenColumns={(cols: string[]) => setHiddenColumns(cols)}
              fields={fields}
              columnSettings={columnSettings}
              accountInfo={accountInfo}
              filterViewCount={segmentContactsCount}
              executeSegment={() => {
                fetchContactCount();
              }}
              contactsCount={contactsCount}
            />
          }
          emptyComponent={
            <EmptyState
              minHeight="calc(100vh - 292px)"
              title={`${t('empty_state.title.no_contacts_to_display')}`}
              message={
                isContactListsError || isExecuteSegmentError || isSegmentError
                  ? `${t('empty_state.message.network_error')}`
                  : !segmentData
                  ? `${t('empty_state.message.new_search')}`
                  : `${t('empty_state.message.no_contact_matches')}`
              }
              imgSrc="empty-state-contacts.svg"
            />
          }
        />
      </TablePane>
    </SegmentContainer>
  );
};

export default CreateSegment;
